import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Receipt,
  Percent,
  Wallet2,
  TrendingUp,
  DollarSign,
  BarChart3,
  Coins,
  RefreshCcw,
  ShieldCheck,
  AlertCircle,
  Mail,
  Share2,
  Banknote,
  PiggyBank,
  LogOut,
  Home,
} from "lucide-react";
import { useAuthStore } from "../../store/auth";
import { api } from "../../api/greenclub";

const DashboardClinica = () => {
  const { user, logout } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [payoutValue, setPayoutValue] = useState("");
  const [payoutObservation, setPayoutObservation] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getClinicaDashboard();
        if (data.success) setDashboardData(data);
        else setError(data.error || "Falha ao carregar dados.");
      } catch (err) {
        console.error("Erro ao carregar dados da clínica:", err);
        setError(
          "Não foi possível carregar os dados da clínica. Tente novamente mais tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);
  const resumoCupons = useMemo(() => {
    if (!dashboardData?.cupons?.length) {
      return { total: 0, ativos: 0, retornoEstimado: 0 };
    }

    const ativos = dashboardData.cupons.filter((cupom) => {
      if (cupom.uso_maximo === null || cupom.uso_maximo === undefined) {
        return true;
      }
      // Corrigido para usar o campo correto 'usos_atuais'
      return (cupom.usos_atuais || 0) < cupom.uso_maximo;
    }).length;

    const retornoEstimado = dashboardData.cupons.reduce((acc, cupom) => {
      const usosRestantes = Math.max(
        0,
        (cupom.uso_maximo || 0) - (cupom.usos_atuais || 0)
      );
      const eIlimitado = cupom.uso_maximo === null;
      const remaining = eIlimitado ? 100 : usosRestantes; // Estimar para 100 usos se for ilimitado
      const retornoPorUso =
        cupom.comissao?.tipo === "valor_fixo"
          ? cupom.comissao.valor
          : ((cupom.produtos_vinculados?.[0]?.preco || 0) *
              (cupom.comissao?.valor || 0)) /
            100;
      return acc + remaining * retornoPorUso;
    }, 0);

    return {
      total: dashboardData.cupons.length,
      ativos,
      retornoEstimado,
    };
  }, [dashboardData?.cupons]);
  const resumoComissoes = useMemo(() => {
    if (!dashboardData?.comissoes?.resumo) {
      return {
        pendente: 0,
        emProcessamento: 0,
        resgatado: 0,
        pendentes: 0,
      };
    }

    const {
      totalPendente, // Disponível para saque
      totalEmProcessamento,
      totalResgatado,
      quantidadePendentes,
      quantidadePagas,
    } = dashboardData.comissoes.resumo;

    return {
      pendente: totalPendente,
      emProcessamento: totalEmProcessamento,
      resgatado: totalResgatado,
      pendentes: quantidadePendentes,
    };
  }, [dashboardData?.comissoes?.resumo]);
  const handlePayoutRequest = async () => {
    const valueNumber = Number(payoutValue.replace(",", "."));

    if (!valueNumber || valueNumber <= 0) {
      alert("Informe um valor válido para solicitar o resgate.");
      return;
    }

    // 1. VERIFICAÇÃO: O valor solicitado não pode ser maior que o pendente.
    if (valueNumber > resumoComissoes.pendente) {
      alert(
        `O valor solicitado (R$ ${valueNumber.toFixed(
          2
        )}) é maior que a sua verba pendente (R$ ${resumoComissoes.pendente.toFixed(
          2
        )}).`
      );
      return;
    }

    try {
      setIsRequestingPayout(true);
      const response = await api.requestClinicPayout({
        valor: valueNumber,
        observacao: payoutObservation || undefined,
      });

      setSuccessMessage(
        response.message || "Solicitação de resgate registrada com sucesso!"
      );
      setPayoutValue("");
      setPayoutObservation("");

      // 2. ATUALIZAÇÃO: Atualiza o estado local para refletir a mudança imediatamente.
      // Isso move o valor de "pendente" para "pago" na UI, aguardando a confirmação do admin.
      setDashboardData((prevData) => {
        const novosSaques = [
          {
            id: `temp-${Date.now()}-${Math.random()}`, // CORREÇÃO: Adiciona um número aleatório para garantir uma chave única
            valor: valueNumber,
            observacao: payoutObservation,
            status: "pendente",
            criado_em: new Date().toISOString(),
          },
          ...(prevData.saques.results || []),
        ];

        // CORREÇÃO: Recria o objeto de resumo para forçar a atualização da UI
        const novoResumo = {
          ...prevData.comissoes.resumo,
          // Garante que a operação seja sempre numérica para evitar concatenação de strings
          totalPendente:
            Number(prevData.comissoes.resumo.totalPendente || 0) - valueNumber,
          totalEmProcessamento:
            Number(prevData.comissoes.resumo.totalEmProcessamento || 0) +
            valueNumber,
        };

        return {
          ...prevData,
          comissoes: {
            ...prevData.comissoes,
            resumo: novoResumo,
          },
          saques: {
            ...prevData.saques,
            results: novosSaques,
          },
        };
      });
    } catch (err) {
      console.error("Erro ao solicitar saque:", err);
      alert(
        err?.message || "Não foi possível registrar a solicitação de resgate."
      );
    } finally {
      setIsRequestingPayout(false);
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
          <p className="mt-4 text-emerald-700 font-medium">
            Carregando visão da clínica...
          </p>
        </div>
      </div>
    );
  }
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-white flex items-center justify-center">
        <div className="bg-white border border-red-200 text-red-700 px-6 py-4 rounded-lg shadow-sm flex items-center gap-3">
          <AlertCircle className="w-6 h-6" />
          <span>
            {error || "Não foi possível carregar os dados da clínica."}
          </span>
        </div>
      </div>
    );
  }

  const { clinic } = dashboardData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-white">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-sm border-b border-emerald-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-emerald-800">
                  Visão da Clínica
                </h1>
                <p className="text-sm text-emerald-600">
                  Monitoramento completo dos seus cupons, vendas e incentivos
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-lg text-sm font-medium">
                {clinic?.nome || user?.nome}
              </div>
              <a
                href="/"
                title="Voltar à seleção de portal"
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition-colors"
              >
                <Home className="w-5 h-5" />
              </a>
              <button
                onClick={logout}
                title="Sair"
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-emerald-700 text-sm underline"
            >
              Fechar
            </button>
          </div>
        )}

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DashboardCard
              icon={Percent}
              title="Cupons Ativos"
              value={`${resumoCupons.ativos} / ${resumoCupons.total}`}
              subtitle={`Potencial de Incentivo: R$ ${resumoCupons.retornoEstimado.toLocaleString(
                "pt-BR",
                { minimumFractionDigits: 2 }
              )}`}
              accent="from-cyan-500 to-cyan-600"
            />
            <DashboardCard
              icon={Coins}
              title="Verba Resgatada"
              value={`R$ ${(
                resumoComissoes.emProcessamento + resumoComissoes.resgatado
              ).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
              })}`}
              subtitle="Solicitações + pagamentos"
              accent="from-amber-500 to-orange-500"
            />
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          <div className="lg:col-span-2 bg-white border border-emerald-100 shadow-sm rounded-2xl p-6">
            <SectionHeader
              icon={Share2}
              title="Cupons da Clínica"
              description="Gerencie os cupons disponíveis hoje e deixe tudo pronto para vincular produtos futuramente"
            />

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full divide-y divide-emerald-100 text-sm">
                <thead>
                  <tr className="text-emerald-800">
                    <th className="px-4 py-3 text-left">Cupom</th>
                    <th className="px-4 py-3 text-left">Produto</th>
                    <th className="px-4 py-3 text-left">Uso / Limite</th>
                    <th className="px-4 py-3 text-left">Incentivo por Uso</th>
                    <th className="px-4 py-3 text-left">Disponível</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-50">
                  {dashboardData.cupons.map((coupon) => {
                    const usosAtuais = coupon.usos_atuais || 0;
                    // CORREÇÃO: Verifica se uso_maximo é null ou undefined para ser ilimitado
                    const eIlimitado = coupon.uso_maximo == null;

                    const disponibilidade = eIlimitado
                      ? "Uso ilimitado"
                      : `${Math.max(
                          0,
                          (coupon.uso_maximo || 0) - usosAtuais
                        )} restantes`;

                    const produtos = coupon.produtos_vinculados || [];
                    const comissao = coupon.comissao;
                    const progressWidth =
                      coupon.uso_maximo && coupon.uso_maximo > 0
                        ? Math.min(100, (usosAtuais / coupon.uso_maximo) * 100)
                        : usosAtuais > 0 // CORREÇÃO: Usa a variável 'usosAtuais' que já lê 'usos_atuais'
                        ? 100
                        : 0;

                    let retornoPorUso = 0;
                    if (comissao?.tipo === "valor_fixo") {
                      retornoPorUso = comissao.valor || 0;
                    } else if (
                      comissao?.tipo === "percentual" &&
                      produtos.length > 0
                    ) {
                      retornoPorUso =
                        (produtos[0].preco * (comissao.valor || 0)) / 100;
                    }

                    return (
                      <tr
                        key={coupon._id}
                        className="hover:bg-emerald-50/50 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="font-semibold text-emerald-800">
                              {coupon.codigo}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {produtos.length > 0 ? (
                            <ul className="space-y-1">
                              {produtos.map((p) => (
                                <li
                                  key={p._id}
                                  className="text-xs text-gray-700"
                                >
                                  - {p.nome}{" "}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-xs text-gray-500 italic">
                              Produto ainda não vinculado • pronto para
                              configuração futura
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-700">
                            {usosAtuais} usos /{" "}
                            <span className="text-gray-500 font-mono">
                              {eIlimitado ? "∞" : coupon.uso_maximo}
                            </span>
                            <div className="w-full bg-emerald-50 rounded-full h-2 mt-2">
                              <div
                                className="bg-emerald-500 h-2 rounded-full"
                                style={{ width: `${progressWidth}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-semibold">
                          {retornoPorUso > 0
                            ? `R$ ${retornoPorUso.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                              })}`
                            : "N/A"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {disponibilidade}
                          {coupon.valido_ate && (
                            <span className="block text-xs text-gray-500">
                              até{" "}
                              {new Date(coupon.valido_ate).toLocaleDateString(
                                "pt-BR"
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-emerald-100 shadow-sm rounded-2xl p-6">
              <SectionHeader
                icon={BarChart3}
                title="Resumo da Verba de Marketing"
                description="Acompanhe o que já foi repassado e os valores pendentes"
              />

              <div className="mt-4 space-y-4">
                <SummaryRow
                  icon={Coins}
                  label="Verba pendente"
                  value={`R$ ${resumoComissoes.pendente.toLocaleString(
                    "pt-BR",
                    { minimumFractionDigits: 2 }
                  )}`}
                  caption={`${resumoComissoes.pendentes} créditos aguardando repasse`}
                />
                <SummaryRow
                  icon={RefreshCcw}
                  label="Verba em processamento"
                  value={`R$ ${(
                    resumoComissoes.emProcessamento || 0
                  ).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                  caption="Solicitações de resgate em análise"
                />
                <SummaryRow
                  icon={Wallet2}
                  label="Verba resgatada (paga)"
                  value={`R$ ${(resumoComissoes.resgatado || 0).toLocaleString(
                    "pt-BR",
                    {
                      minimumFractionDigits: 2,
                    }
                  )}`}
                  caption={`${dashboardData.comissoes.resumo.quantidadePagas} incentivos processados`}
                />
              </div>
            </div>

            <div className="bg-white border border-emerald-100 shadow-sm rounded-2xl p-6">
              <SectionHeader
                icon={PiggyBank}
                title="Solicitar Resgate de Verba"
                description="Envie sua solicitação para resgatar sua verba de marketing disponível"
              />

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor desejado
                  </label>
                  <input
                    value={payoutValue}
                    onChange={(e) => setPayoutValue(e.target.value)}
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-emerald-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações (opcional)
                  </label>
                  <textarea
                    value={payoutObservation}
                    onChange={(e) => setPayoutObservation(e.target.value)}
                    className="w-full border border-emerald-200 rounded-lg px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Informações adicionais para a equipe financeira"
                  />
                </div>

                <button
                  onClick={handlePayoutRequest}
                  disabled={isRequestingPayout}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isRequestingPayout ? (
                    <>
                      <RefreshCcw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Solicitar resgate
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500">
                  Um e-mail será enviado para a equipe do GreenClub com a sua
                  solicitação. Entraremos em contato para confirmar os dados e
                  agendar o repasse.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          <div className="lg:col-span-2 bg-white border border-emerald-100 shadow-sm rounded-2xl p-6">
            <SectionHeader
              icon={Receipt}
              title="Últimos Incentivos Pendentes"
              description="Pedidos aguardando geração de verba"
            />

            {dashboardData.comissoes.pendentes.results.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                title="Nenhum incentivo pendente"
                description="Assim que novas vendas forem confirmadas, elas aparecerão aqui."
              />
            ) : (
              <div className="mt-4 space-y-3">
                {dashboardData.comissoes.pendentes.results.map((commission) => (
                  <div
                    key={commission.id}
                    className="border border-emerald-100 rounded-xl py-3 px-4 bg-emerald-50/60"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">
                          Pedido #{commission.pedido_id}
                        </p>
                        <p className="text-xs text-emerald-600">
                          Venda em{" "}
                          {new Date(commission.pedido_data).toLocaleDateString(
                            "pt-BR"
                          )}{" "}
                          • Incentivo gerado em{" "}
                          {new Date(commission.criada_em).toLocaleDateString(
                            "pt-BR"
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-700">
                          R${" "}
                          {commission.valor.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                        <p className="text-xs text-emerald-600">
                          {commission.percentual}% sobre R${" "}
                          {commission.pedido_total.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-emerald-100 shadow-sm rounded-2xl p-6">
            <SectionHeader
              icon={Banknote}
              title="Histórico de Resgates"
              description="Acompanhe suas solicitações e o status do repasse"
            />

            {dashboardData.saques.results.length === 0 ? (
              <EmptyState
                icon={PiggyBank}
                title="Nenhum resgate solicitado"
                description="Quando você solicitar um pagamento, ele será exibido aqui."
              />
            ) : (
              <div className="mt-4 space-y-3">
                {dashboardData.saques.results.map((payout) => (
                  <div
                    key={payout.id}
                    className="border border-emerald-100 rounded-xl px-4 py-3"
                  >
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">
                          R${" "}
                          {payout.valor.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                        <p className="text-xs text-gray-500">
                          Solicitação em{" "}
                          {new Date(payout.criado_em).toLocaleDateString(
                            "pt-BR"
                          )}
                        </p>
                        {payout.observacao && (
                          <p className="text-xs text-gray-600 mt-1">
                            {payout.observacao}
                          </p>
                        )}
                      </div>
                      <span className={getStatusBadgeClass(payout.status)}>
                        {translateStatus(payout.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.section>
      </div>
    </div>
  );
};
export const DashboardCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  accent,
}) => (
  <div className="bg-white border border-emerald-100 shadow-sm rounded-2xl">
    <div className={`rounded-t-2xl h-2 bg-gradient-to-r ${accent}`} />
    <div className="p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
        <Icon className="w-6 h-6 text-emerald-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
          {title}
        </p>
        <p className="text-xl font-bold text-emerald-800">{value}</p>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
    </div>
  </div>
);
export const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
      <Icon className="w-5 h-5 text-emerald-600" />
    </div>
    <div>
      <h2 className="text-lg font-semibold text-emerald-900">{title}</h2>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  </div>
);
export const SummaryRow = ({ icon: Icon, label, value, caption }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
      <Icon className="w-5 h-5 text-emerald-600" />
    </div>
    <div>
      <p className="text-sm font-semibold text-emerald-900">{label}</p>
      <p className="text-lg font-bold text-emerald-700">{value}</p>
      <p className="text-xs text-gray-500">{caption}</p>
    </div>
  </div>
);
export const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="border border-dashed border-emerald-200 rounded-xl p-6 text-center text-sm text-gray-500">
    <Icon className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
    <p className="font-medium text-emerald-800">{title}</p>
    <p className="text-xs text-gray-500">{description}</p>
  </div>
);
function getStatusBadgeClass(status) {
  const base = "px-3 py-1 rounded-full text-xs font-semibold";
  switch (status) {
    case "pago":
      return `${base} bg-emerald-100 text-emerald-700`;
    case "processando":
      return `${base} bg-blue-100 text-blue-700`;
    case "rejeitado":
      return `${base} bg-red-100 text-red-700`;
    default:
      return `${base} bg-amber-100 text-amber-700`;
  }
}
function translateStatus(status) {
  const map = {
    pendente: "Pendente",
    processando: "Processando",
    pago: "Pago",
    rejeitado: "Rejeitado",
  };
  return map[status] || status;
}
export default DashboardClinica;
